// Ponto único de sanitização do que o front envia ao Sentry (SAT-sentry, D16).
//
// Aplicado a TODO evento, transação, breadcrumb e log via beforeSend / beforeSendTransaction /
// beforeBreadcrumb / beforeSendLog (ver sentryInit.js). Um interceptor ou console.error novo passa
// pelo mesmo filtro: o furo não reabre por esquecimento de quem escreve o call-site.
//
// Sem dependência do Sentry de propósito: é função pura sobre objetos, testável sem init.

export const REDACTED = '[redacted]';
export const REDACTED_BODY = '[redacted: corpo não estruturado]';

// Chaves sensíveis (comparação case-insensitive, ignorando "-" e "_"). É a ÚNICA lista: o teste
// `sentrySanitize.test.ts` falha se alguma sair daqui.
export const SENSITIVE_KEYS: readonly string[] = [
  'password',
  'senha',
  'newPassword',
  'passwordConfirmation',
  'confirmPassword',
  'token',
  'access_token',
  'refresh_token',
  'refreshToken',
  'authToken',
  'passwordResetToken',
  'Authorization',
  'Cookie',
  'Set-Cookie',
  'x-api-key',
];

const normalizeKey = (key: string) => key.toLowerCase().replace(/[-_]/g, '');

const SENSITIVE_KEY_SET = new Set(SENSITIVE_KEYS.map(normalizeKey));
// Variações não listadas (csrfToken, senhaAtual, clientSecret...): terminam em "token" ou contêm password/senha/secret.
const SENSITIVE_KEY_PATTERN = /(token$|password|passwd|senha|secret)/;

export const isSensitiveKey = (key: string): boolean => {
  const normalized = normalizeKey(key);
  return SENSITIVE_KEY_SET.has(normalized) || SENSITIVE_KEY_PATTERN.test(normalized);
};

// Padrões de URL (path e query). Aplicados a qualquer string do evento.
export const SENSITIVE_URL_PATTERNS: ReadonlyArray<{ name: string; pattern: RegExp; replacement: string }> = [
  {
    name: 'redefinir-senha (página)',
    pattern: /(\/conta\/redefinir-senha\/)[^/?#\s"'`]+/gi,
    replacement: `$1${REDACTED}`,
  },
  {
    name: 'validação de token de redefinição (API)',
    pattern: /(\/v1\/account\/password\/token\/)[^/?#\s"'`]+/gi,
    replacement: `$1${REDACTED}`,
  },
  {
    name: 'segredo na query/fragmento (SSO, token, senha)',
    pattern:
      /([?&#](?:access_token|refresh_token|token|authtoken|refreshtoken|passwordresettoken|password|newpassword|senha)=)[^&#\s"'`]*/gi,
    replacement: `$1${REDACTED}`,
  },
];

export const sanitizeUrl = (text: string): string =>
  SENSITIVE_URL_PATTERNS.reduce((acc, { pattern, replacement }) => acc.replace(pattern, replacement), text);

// Rotas em que o replay NÃO pode gravar: o evento Meta do rrweb (tipo 4) leva `location.href` com o token e o
// SDK não chama `beforeAddRecordingEvent` para ele. Fora do alcance da sanitização, então o replay fica desligado.
// (Regex sem a flag `g`: `.test` é sem estado.)
export const SENSITIVE_ROUTE_PATTERNS: readonly RegExp[] = [/\/conta\/redefinir-senha\//i, /\/login-sso\/callback/i];

export const isSensitiveLocation = (href: string): boolean =>
  SENSITIVE_ROUTE_PATTERNS.some(pattern => pattern.test(href)) || sanitizeUrl(href) !== href;

// Chaves cujo valor string, se não for estruturado, é descartado por inteiro.
const BODY_KEYS = new Set(['body', 'data', 'requestbody', 'responsebody', 'payload']);

const MAX_DEPTH = 15;
const URLENCODED_PATTERN = /^[^\s=&/?:]+=[^&]*(&[^\s=&/?:]+=[^&]*)*$/;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' &&
  value !== null &&
  (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null);

const sanitizeString = (value: string, key: string | undefined, seen: WeakSet<object>, depth: number): unknown => {
  const trimmed = value.trim();

  // marcador já aplicado por este módulo ou pelo interceptor: não reprocessa
  if (/^\[(redacted|omitido)/.test(trimmed)) return value;

  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      return JSON.stringify(walk(JSON.parse(trimmed), undefined, seen, depth + 1));
    } catch {
      // não é JSON válido: cai para os outros tratamentos
    }
  }

  if (URLENCODED_PATTERN.test(trimmed) && trimmed.includes('=')) {
    try {
      const params = new URLSearchParams(trimmed);
      const out = new URLSearchParams();
      params.forEach((v, k) => out.append(k, isSensitiveKey(k) ? REDACTED : sanitizeUrl(v)));
      return out.toString().replace(/%5Bredacted%5D/gi, REDACTED);
    } catch {
      // segue
    }
  }

  if (key && BODY_KEYS.has(normalizeKey(key)) && value.length > 0 && !/^https?:|^\//i.test(trimmed)) {
    return REDACTED_BODY;
  }

  return sanitizeUrl(value);
};

const walk = (value: unknown, key: string | undefined, seen: WeakSet<object>, depth: number): unknown => {
  if (typeof value === 'string') return sanitizeString(value, key, seen, depth);
  if (value === null || typeof value !== 'object') return value;
  if (depth > MAX_DEPTH) return '[max depth]';

  if (typeof FormData !== 'undefined' && value instanceof FormData) {
    // só os nomes dos campos: valores (assinatura, arquivo, senha) nunca saem
    return { formDataFields: Array.from(new Set(Array.from(value.keys()))) };
  }
  if (typeof URLSearchParams !== 'undefined' && value instanceof URLSearchParams) {
    return sanitizeString(value.toString(), key, seen, depth);
  }

  if (seen.has(value)) return '[circular]';
  seen.add(value);

  if (Array.isArray(value)) return value.map(item => walk(item, key, seen, depth + 1));

  if (value instanceof Error) {
    const out: Record<string, unknown> = { name: value.name, message: sanitizeUrl(value.message) };
    Object.keys(value).forEach(k => {
      out[k] = isSensitiveKey(k)
        ? REDACTED
        : walk((value as unknown as Record<string, unknown>)[k], k, seen, depth + 1);
    });
    return out;
  }

  if (
    !isPlainObject(value) &&
    typeof (value as { forEach?: unknown }).forEach === 'function' &&
    typeof (value as { toJSON?: unknown }).toJSON === 'function'
  ) {
    // AxiosHeaders e similares: serializa para objeto e trata como tal
    return walk((value as { toJSON: () => unknown }).toJSON(), key, seen, depth + 1);
  }

  const out: Record<string, unknown> = {};
  Object.keys(value as Record<string, unknown>).forEach(k => {
    const v = (value as Record<string, unknown>)[k];
    out[k] = isSensitiveKey(k) ? REDACTED : walk(v, k, seen, depth + 1);
  });
  return out;
};

export const sanitizeValue = <T = unknown>(value: T): T => walk(value, undefined, new WeakSet(), 0) as T;

// Os tipos do Sentry entram só como forma estrutural; o objeto devolvido é do mesmo formato.
export const sanitizeEvent = <T extends object>(event: T): T => walk(event, undefined, new WeakSet(), 0) as T;
export const sanitizeBreadcrumb = <T extends object>(breadcrumb: T): T =>
  walk(breadcrumb, undefined, new WeakSet(), 0) as T;
export const sanitizeLog = <T extends object>(log: T): T => walk(log, undefined, new WeakSet(), 0) as T;

// Replay: eventos rrweb. Só os que carregam URL/dados de navegação (Meta = 4, Custom = 5), para não
// percorrer o snapshot do DOM inteiro a cada mutação.
export const sanitizeRecordingEvent = <T extends { type?: number }>(event: T): T => {
  if (event?.type === 4 || event?.type === 5) return walk(event, undefined, new WeakSet(), 0) as T;
  return event;
};

type SentryEventLike = { type?: string; level?: string };

// Evento mínimo e FIXO: nada do evento original. Nunca passa de novo pelo sanitizador.
export const buildSanitizeFailedEvent = (kind: string, error: unknown) => ({
  message: 'sentry_sanitize_failed',
  level: 'error' as const,
  tags: { sanitize_failed_for: kind },
  extra: { sanitizerError: error instanceof Error ? error.name : typeof error },
});

export interface SanitizeHooks {
  beforeSend: (event: any) => any;
  beforeSendTransaction: (event: any) => any;
  beforeBreadcrumb: (breadcrumb: any) => any;
  beforeSendLog: (log: any) => any;
  beforeAddRecordingEvent: (event: any) => any;
  processReplayEvent: (event: any) => any;
}

// Fail-closed: se o sanitizador lançar, o original é DESCARTADO. Para evento de erro, no lugar vai o
// evento mínimo fixo (telemetria do bug do sanitizador). Para breadcrumb/transação/log/replay o
// descarte é sem substituto, mas `onFailure` é avisado (no sentryInit vira o mesmo evento mínimo).
// `onFailure` roda uma vez por vez (trava de reentrada), então um evento mínimo que falhasse não gera laço.
export const createSanitizeHooks = (
  onFailure?: (kind: string, error: unknown) => void,
  // navegação interna para rota sensível (ex.: parar o replay ANTES da troca de rota; o hook de navegação do SDK é síncrono)
  onSensitiveNavigation?: () => void,
): SanitizeHooks => {
  let reportando = false;
  const falhou = (kind: string, error: unknown) => {
    if (!onFailure || reportando) return;
    reportando = true;
    try {
      onFailure(kind, error);
    } catch {
      // nunca propaga
    } finally {
      reportando = false;
    }
  };

  return {
    beforeSend: (event: SentryEventLike) => {
      try {
        return sanitizeEvent(event);
      } catch (error) {
        return buildSanitizeFailedEvent(event?.type ?? 'error', error);
      }
    },
    beforeSendTransaction: event => {
      try {
        return sanitizeEvent(event);
      } catch (error) {
        falhou('transaction', error);
        return null;
      }
    },
    beforeBreadcrumb: breadcrumb => {
      try {
        if (breadcrumb?.category === 'navigation' && isSensitiveLocation(String(breadcrumb.data?.to ?? ''))) {
          try {
            onSensitiveNavigation?.();
          } catch {
            // parar o replay nunca derruba o breadcrumb
          }
        }
        return sanitizeBreadcrumb(breadcrumb);
      } catch (error) {
        falhou('breadcrumb', error);
        return null;
      }
    },
    beforeSendLog: log => {
      try {
        return sanitizeLog(log);
      } catch (error) {
        falhou('log', error);
        return null;
      }
    },
    beforeAddRecordingEvent: event => {
      try {
        return sanitizeRecordingEvent(event);
      } catch (error) {
        falhou('replay', error);
        return null;
      }
    },
    // O envelope de metadados do replay (`replay_event.urls`) não passa por beforeSend nem por
    // beforeAddRecordingEvent: só por event processors. Registrado com Sentry.addEventProcessor.
    processReplayEvent: (event: SentryEventLike) => {
      if (event?.type !== 'replay_event') return event;
      try {
        return sanitizeEvent(event);
      } catch (error) {
        falhou('replay_event', error);
        return null;
      }
    },
  };
};
